<script setup>
import {setFilterFields} from "../../../../helpers/common";
import {onMounted, reactive, ref, watch} from "vue";
import ValidationErrors from "../../../common/ValidationErrors.vue";
import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import {usePortalFilterAndPaginationStore} from "../../../../stores/partners/filterAndPagination";
import {useRoute} from "vue-router";

const portalContentAndDesignStore = usePortalContentAndDesignStore()
const filterAndPaginationStore = usePortalFilterAndPaginationStore()

    const props = defineProps({
        filterList: {
            type: Object,
            default: {}
        }
    })

    let searchQuery = ref('')
    let filters = reactive({})
    const route = useRoute()
    
    // Track if initial setup is complete (to distinguish from user interaction)
    let isInitialized = ref(false)

    const filterList = props.filterList

    onMounted(() => {
        // Reset the filter changed flag for this route on mount
        filterAndPaginationStore.resetFiltersChanged(route.name)
        setFilterFields(filters, filterList)
        // Mark as initialized after a tick to allow the initial watch to fire first
        setTimeout(() => {
            isInitialized.value = true
        }, 0)
    })

    const assignDatesToFilterVars = (dates) => {
        filters.date_from = dates.dateFrom
        filters.date_to = dates.dateTo
    }

    function fetchData() {
        filterAndPaginationStore.getFiltersAndPaginationData(route.name, {...filters, search: searchQuery.value})
    }

    watch(filters, (updatedFilters) => {
        filterAndPaginationStore.setFilters(updatedFilters)
        // Only mark filters as user-changed after initial setup is complete
        if (isInitialized.value) {
            filterAndPaginationStore.markFiltersChanged(route.name)
        }
        fetchData();
    })

    watch(searchQuery, () => {
        filterAndPaginationStore.setSearchParam(searchQuery.value)
        // Mark filters as changed when user types in search
        if (isInitialized.value) {
            filterAndPaginationStore.markFiltersChanged(route.name)
        }
    })

    const icons = portalContentAndDesignStore.portalLayoutSettings.portal_layout_icons

    const translateFilterOption = (fieldName, optionLabel, optionValue) => {
        const t = portalContentAndDesignStore.translationSettings;
        
        // Translate "Any" option
        if (optionLabel === 'Any' || optionValue === '') {
            return t.portal_translation_general_filter_any || 'Any';
        }
        
        // Translate status filter options
        if (fieldName === 'status') {
            const statusLower = optionLabel.toLowerCase();
            const valueLower = (optionValue || '').toLowerCase();
            
            if (statusLower === 'submitted' || valueLower === 'submitted') {
                return t.portal_translation_general_lead_status_submitted || optionLabel;
            }
            if (statusLower === 'rejected' || valueLower === 'rejected') {
                return t.portal_translation_general_lead_status_rejected || optionLabel;
            }
            if (statusLower === 'converted' || valueLower === 'converted') {
                return t.portal_translation_general_lead_status_converted || optionLabel;
            }
        }
        
        // Translate action filter options
        if (fieldName === 'action') {
            const actionLower = optionLabel.toLowerCase();
            const valueLower = (optionValue || '').toLowerCase();
            
            if (actionLower === 'sale' || valueLower === 'everythingdifferentthansignup' || optionValue === 'everythingDifferentThanSignup') {
                return t.portal_translation_general_action_sale || optionLabel;
            }
            if (actionLower === 'signup' || valueLower === 'signup') {
                return t.portal_translation_general_action_signup || optionLabel;
            }
        }
        
        // Return original label if no translation found
        return optionLabel;
    }

</script>

<template>
    <div class="flex flex-col md:flex-row gap-4 flex-1">
        <!-- Search input - takes remaining space -->
        <div class="relative flex-1">
            <div
                class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none text-opacity-50"
                :class="portalContentAndDesignStore.getTextColorClass"
            >
                <svg
                    v-if="icons === 'bold'"
                    class="w-4 h-4"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                >
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M11 2c4.968 0 9 4.032 9 9s-4.032 9-9 9-9-4.032-9-9 4.032-9 9-9zm8.485 16.071l2.829 2.828-1.415 1.415-2.828-2.829 1.414-1.414z"/>
                </svg>
                <svg
                    v-else
                    class="w-4 h-4"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                >
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M11 2c4.968 0 9 4.032 9 9s-4.032 9-9 9-9-4.032-9-9 4.032-9 9-9zm0 16c3.867 0 7-3.133 7-7 0-3.868-3.133-7-7-7-3.868 0-7 3.132-7 7 0 3.867 3.132 7 7 7zm8.485.071l2.829 2.828-1.415 1.415-2.828-2.829 1.414-1.414z"/>
                </svg>
            </div>
            <input
                v-model="searchQuery"
                type="text"
                name="search"
                id="search"
                :class="['block pl-9 w-full text-sm border-gray-300 transition-shadow focus:ring-0 focus:border-gray-400', portalContentAndDesignStore.getPortalLayoutCornersClass]"
                :placeholder="portalContentAndDesignStore.translationSettings.portal_translation_general_search"
                @keyup.enter="fetchData"
            >
        </div>

        <!-- Filters and button - aligned to the right -->
        <div class="flex flex-col md:flex-row gap-4 md:flex-shrink-0">
            <template v-for="(filter, fieldName) in filterList">
            <!-- select -->
                <div
                    v-if="filter.type === 'select'"
                    class="flex relative w-full md:w-44">
                    <div class="relative w-full">
                        <span
                            :key="filter.type + '-' + filter.label"
                            class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none text-opacity-50"
                            :class="portalContentAndDesignStore.getTextColorClass"
                        >
                            <svg
                                v-if="icons === 'bold'"
                                class="w-4 h-4"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                            >
                                <path fill="none" d="M0 0H24V24H0z"/>
                                <path d="M21 4L21 6 20 6 14 15 14 22 10 22 10 15 4 6 3 6 3 4z"/>
                            </svg>
                            <svg
                                v-else
                                class="w-4 h-4" fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24"
                                height="24"
                            >
                                <path fill="none" d="M0 0H24V24H0z"/>
                                <path d="M21 4v2h-1l-5 7.5V22H9v-8.5L4 6H3V4h18zM6.404 6L11 12.894V20h2v-7.106L17.596 6H6.404z"/>
                            </svg>
                        </span>
                        <select
                            v-model="filters[fieldName]"
                            :id="fieldName"
                            :name="fieldName"
                            :class="['block pl-9 w-full text-sm border-gray-300 transition-shadow focus:ring-0 focus:border-gray-400 capitalize', portalContentAndDesignStore.getPortalLayoutCornersClass]"
                        >
                            <option v-for="(option) in filter.select_options"
                                    :key="'option-' + option.value"
                                    :selected="option === filter.default_value"
                                    :value="option.value"
                            >
                                {{ translateFilterOption(fieldName, option.label, option.value) }}
                            </option>
                        </select>
                    </div>
                </div>
            <!-- ./ select -->
            <!-- range datepicker -->
            <div
                v-if="filter.type === 'range_datepicker'"
                class="inline-flex relative w-full md:w-64"
            >

                <date-range-picker
                    @date-changed="assignDatesToFilterVars"
                    :date_from="filter.date_from"
                    :date_to="filter.date_to"
                    class="flex-1"
                />
                <div
                    class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none text-opacity-50"
                    :class="portalContentAndDesignStore.getTextColorClass"
                >
                    <svg
                        v-if="icons === 'bold'"
                        class="w-4 h-4"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                    >
                        <path fill="none" d="M0 0h24v24H0z"/>
                        <path d="M2 11h20v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-9zm15-8h4a1 1 0 0 1 1 1v5H2V4a1 1 0 0 1 1-1h4V1h2v2h6V1h2v2z"/>
                    </svg>
                    <svg
                        v-else
                        class="w-4 h-4"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                    >
                        <path fill="none" d="M0 0h24v24H0z"/>
                        <path d="M17 3h4a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4V1h2v2h6V1h2v2zm-2 2H9v2H7V5H4v4h16V5h-3v2h-2V5zm5 6H4v8h16v-8z"/>
                    </svg>
                </div>

            </div>
            <!-- ./ range datepicker -->
            </template>

            <div class="flex-shrink-0 self-stretch">
                <btn
                    class="h-full px-4"
                    icon-name="search"
                    :type="portalContentAndDesignStore.getPortalLayoutButtonColorClass"
                    :corners="portalContentAndDesignStore.getPortalLayoutCornersClass"
                    @click="fetchData"
                />
            </div>
        </div>

        <validation-errors
            :errors="filterAndPaginationStore.validationErrors"
            class="mt-2.5 w-full"
        />
    </div>
</template>
